// import node module libraries
import { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { Image, Navbar, Nav, Container } from "react-bootstrap";
import { useMediaQuery } from "react-responsive";

// import sub layout components
import QuickMenu from "layouts/QuickMenu";
import DarkLightMode from "layouts/DarkLightMode";

// import media files
import Logo from "assets/images/brand/logo/logo-icon.svg";

// import data files
import { NavbarLinks } from "routes/marketing/NavbarDefault";
import { PATH_AUTH, PATH_DASHBOARD, PATH_PAGE } from "routes/Paths";
import useAuth from "hooks/useAuth";
import RoleBasedGuard from "guards/RoleGuard";

const NavbarDefault = ({ headerstyle }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  const isDesktop = useMediaQuery({
    query: "(min-width: 1224px)",
  });
  const isLaptop = useMediaQuery({
    query: "(min-width: 1024px)",
  });

  const [expandedMenu, setExpandedMenu] = useState(false);

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if nav link is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <Fragment>
      <Navbar
        onToggle={(collapsed) => setExpandedMenu(collapsed)}
        expanded={expandedMenu}
        expand="lg"
        className={`navbar-modern ${scrolled ? 'navbar-scrolled' : ''}`}
        fixed="top"
      >
        <Container fluid className="navbar-container">
          {/* Logo */}
          <Navbar.Brand as={Link} to="/" className="navbar-brand-modern">
            <div className="logo-wrapper">
              <Image src={Logo} height={42} alt="Hope Arthritis Academy" />
              <span className="brand-text d-none d-sm-inline-block">
                Hope Arthritis <span className="text-primary">Academy</span>
              </span>
            </div>
          </Navbar.Brand>

          {/* Mobile Quick Menu */}
          <div
            className={`navbar-mobile-actions ${
              isAuthenticated
                ? isDesktop || isLaptop
                  ? "d-none"
                  : "d-flex"
                : "d-none"
            }`}
          >
            <QuickMenu />
          </div>

          {/* Mobile Toggle */}
          <Navbar.Toggle 
            aria-controls="navbar-nav" 
            className="navbar-toggle-modern"
          >
            <span className="navbar-toggler-line"></span>
            <span className="navbar-toggler-line"></span>
            <span className="navbar-toggler-line"></span>
          </Navbar.Toggle>

          <Navbar.Collapse id="navbar-nav">
            {/* Main Navigation */}
            <Nav className="navbar-nav-modern mx-auto">
              <Nav.Link 
                as={Link} 
                to={PATH_PAGE.root}
                className={`nav-link-modern ${isActive(PATH_PAGE.root) ? 'active' : ''}`}
              >
                Home
              </Nav.Link>

              <Nav.Link 
                as={Link} 
                to={PATH_DASHBOARD.courses.browse}
                className={`nav-link-modern ${isActive(PATH_DASHBOARD.courses.browse) ? 'active' : ''}`}
              >
                Courses
              </Nav.Link>

              {NavbarLinks.map((item, index) => (
                <RoleBasedGuard key={index + item.menuitem} roles={item.roles}>
                  <Nav.Link 
                    as={Link} 
                    to={item.link}
                    className={`nav-link-modern ${isActive(item.link) ? 'active' : ''}`}
                  >
                    {item.menuitem}
                  </Nav.Link>
                </RoleBasedGuard>
              ))}
            </Nav>

            {/* Right Side Actions */}
            <Nav className="navbar-actions ms-auto">
              {!isAuthenticated && <DarkLightMode className="me-2" />}
              
              {!isAuthenticated ? (
                <div className="auth-buttons">
                  <Nav.Link
                    as={Link}
                    to={PATH_AUTH.login}
                    className="btn btn-link-modern me-2"
                  >
                    Sign In
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to={PATH_AUTH.login}
                    className="btn btn-primary-modern"
                  >
                    Get Started
                  </Nav.Link>
                </div>
              ) : (
                <div
                  className={`user-menu ${
                    isAuthenticated
                      ? isDesktop || isLaptop
                        ? "d-flex"
                        : "d-none"
                      : "d-none"
                  }`}
                >
                  <QuickMenu />
                </div>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <style jsx>{`
        .navbar-modern {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          // padding: 1rem 0;
          transition: all 0.3s ease;
          border-bottom: 1px solid rgba(226, 232, 240, 0.8);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .navbar-modern.navbar-scrolled {
          padding: 0.5rem 0;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .navbar-container {
          padding: 0 2rem;
        }

        /* Logo Styles */
        .navbar-brand-modern {
          padding: 0;
          margin: 0;
        }

        .logo-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .brand-text {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          letter-spacing: -0.02em;
        }

        .brand-text .text-primary {
          color: #F68521 !important;
        }

        /* Navigation Links */
        .navbar-nav-modern {
          gap: 0.5rem;
        }

        .nav-link-modern {
          position: relative;
          color: #64748b !important;
          font-weight: 500;
          font-size: 0.9375rem;
          padding: 0.5rem 1rem !important;
          border:none !important;
          // border-radius: 8px;
          transition: all 0.2s ease;
        }

        .nav-link-modern:hover {
          color: #F68521 !important;
          background: rgba(246, 133, 33, 0.05);
        }

        .nav-link-modern.active {
          color: #F68521 !important;
          background: rgba(246, 133, 33, 0.1);
        }

        .nav-link-modern.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 1rem;
          right: 1rem;
          height: 2px;
          background: #F68521;
          border-radius: 2px 2px 0 0;
        }

        /* Auth Buttons */
        .auth-buttons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-link-modern {
          color: #64748b !important;
          font-weight: 600;
          padding: 0.5rem 1rem !important;
          transition: all 0.2s ease;
          text-decoration: none !important;
        }

        .btn-link-modern:hover {
          color: #F68521 !important;
        }

        .btn-primary-modern {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%) !important;
          color: white !important;
          border: none !important;
          padding: 0.625rem 1.5rem !important;
          border-radius: 10px !important;
          font-weight: 600 !important;
          font-size: 0.9375rem !important;
          transition: all 0.3s ease !important;
          box-shadow: 0 4px 12px rgba(246, 133, 33, 0.25);
          text-decoration: none !important;
        }

        .btn-primary-modern:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(246, 133, 33, 0.35) !important;
        }

        /* Mobile Toggle */
        .navbar-toggle-modern {
          border: none;
          padding: 0.5rem;
          background: transparent;
        }

        .navbar-toggle-modern:focus {
          box-shadow: none;
        }

        .navbar-toggler-line {
          display: block;
          width: 24px;
          height: 2px;
          background: #1e293b;
          border-radius: 2px;
          transition: all 0.3s ease;
          margin: 5px 0;
        }

        .navbar-toggle-modern[aria-expanded="true"] .navbar-toggler-line:nth-child(1) {
          transform: rotate(45deg) translate(6px, 6px);
        }

        .navbar-toggle-modern[aria-expanded="true"] .navbar-toggler-line:nth-child(2) {
          opacity: 0;
        }

        .navbar-toggle-modern[aria-expanded="true"] .navbar-toggler-line:nth-child(3) {
          transform: rotate(-45deg) translate(6px, -6px);
        }

        /* Mobile Styles */
        @media (max-width: 991px) {
          .navbar-container {
            padding: 0 1rem;
          }

          .navbar-nav-modern {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #e2e8f0;
          }

          .navbar-actions {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #e2e8f0;
          }

          .auth-buttons {
            flex-direction: column;
            width: 100%;
            gap: 0.75rem;
          }

          .btn-link-modern,
          .btn-primary-modern {
            width: 100%;
            text-align: center;
            justify-content: center;
          }

          .nav-link-modern {
            padding: 0.75rem 1rem !important;
          }

          .brand-text {
            font-size: 1.125rem;
          }
        }

        @media (max-width: 575px) {
          .brand-text {
            display: none !important;
          }
        }
      `}</style>
    </Fragment>
  );
};

// Specifies the default values for props
NavbarDefault.defaultProps = {
  headerstyle: "navbar-default",
  login: false,
};

// Typechecking With PropTypes
NavbarDefault.propTypes = {
  headerstyle: PropTypes.string,
  login: PropTypes.bool,
};

export default NavbarDefault;